// ========= START: App.js (Corrected with Anti-Echo Logic) =========

// ========= START: App.js - Imports Section =========

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import db from './content_db.json';
import { characters } from './character_db';
import ConversationWindow from './components/ConversationWindow';
import CharacterSelection from './components/CharacterSelection';
import SubtitleControls from './components/SubtitleControls';
import AvatarDisplay from './components/AvatarDisplay';
import WelcomeScreen from './components/WelcomeScreen';
import BpEchoMainMenu from './components/BpEchoMainMenu';
import GradeSelection from './components/GradeSelection';
// UsernameSetupScreen import removed
import { speak } from './utils/tts';
import { extractName, reconcileSpelledName } from './utils/transcript';
import { useSpeechRecognition } from './hooks/useSpeechRecognition';
import audioManager from './utils/audioManager';
import Background from './components/Background';
import { useLang } from './i18n';

// ========= END: App.js - Imports Section =========

// ========= START: Helper Functions (Unchanged) =========
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  if (hour >= 18 && hour < 22) return 'Good evening';
  return 'Hello';
};

const formatUserTranscript = (transcript) => {
  if (!transcript) return '';
  let formatted = transcript.trim();
  let transcriptLower = formatted.toLowerCase();

  const fillerWords = ['um', 'umm', 'uh', 'ah', 'hmm', 'eto', 'ehh'];
  let hasFiller = true;
  while (hasFiller) {
    hasFiller = false;
    for (const filler of fillerWords) {
      if (transcriptLower.startsWith(filler + ' ')) {
        formatted = formatted.substring(filler.length + 1).trim();
        transcriptLower = formatted.toLowerCase();
        hasFiller = true;
        break;
      }
    }
  }

  if (formatted.length === 0) return '';

  const splitPhrases = [
    "how about you", "what about you", "and you", "do you like",
    "what is", "what are", "what kind", "what season", "what color", "what sport",
    "how are you"
  ];

  for (const phrase of splitPhrases) {
    const index = transcriptLower.indexOf(phrase);
    if (index > 0) {
      let part1 = formatUserTranscript(formatted.substring(0, index));
      let part2 = formatUserTranscript(formatted.substring(index));
      const lastCharOfPart1 = part1.slice(-1);
      if (!['.', '?', '!'].includes(lastCharOfPart1)) {
        part1 += '.';
      }
      return `${part1} ${part2}`;
    }
  }

  formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  const lastChar = formatted.slice(-1);
  if (['.', '?', '!'].includes(lastChar)) {
    return formatted;
  }

  const questionWords = ['who', 'what', 'where', 'when', 'why', 'how', 'do', 'are', 'is', 'can', 'will'];
  const firstWord = formatted.split(' ')[0].toLowerCase();
  if (questionWords.includes(firstWord)) {
    formatted += '?';
  } else {
    formatted += '.';
  }
  return formatted;
};

const getRandomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];
// ========= END: Helper Functions (Unchanged) =========


// ========= START: App Component =========

const trackView = (appName) => {
    const token = localStorage.getItem('bplabo_jwt_token');
    if (!token) return;
    const base = window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://bplabo.jp';
    fetch(`${base}/api/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ appName })
    }).catch(() => {});
};

// ========= START: App.js - getInitialPhase Function =========
const getInitialPhase = () => {
  const hash = window.location.hash.substring(1);
  // REMOVED: All 'PRONUNCIATION_CHALLENGE' related phases
  const validPhases = ['WELCOME', 'BP_ECHO_MAIN_MENU', 'CHARACTER_SELECTION', 'GRADE_SELECTION', 'CONVERSATION', 'ENDED'];
  if (hash && validPhases.includes(hash)) {
    return hash;
  }
  return 'WELCOME';
};
// ========= END: App.js - getInitialPhase Function =========

function App() {

  // ========= START: App.js - State and Refs Initialization Section =========
  const [appPhase, setAppPhase] = useState('LOADING');
  const [playerData, setPlayerData] = useState(null);
  const [subtitleMode, setSubtitleMode] = useState('EN');
  const [showSettings, setShowSettings] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentTurn, setCurrentTurn] = useState('NONE');
  const [errorCount, setErrorCount] = useState(0);
  const [context, setContext] = useState({});
  const [activeCharacter, setActiveCharacter] = useState(characters[0]);
  const [botState, setBotState] = useState('idle');
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isMuted, setIsMuted] = useState(audioManager.isMuted); // NEW: Mute state

  const speechRecognition = useSpeechRecognition();
  const { finalTranscript, resetTranscript } = speechRecognition;
  const { t } = useLang();
  // ========= END: App.js - State and Refs Initialization Section =========


  useEffect(() => { trackView('BP-Talk'); }, []);

  const navigate = (newPhase) => {
    if (window.history.state?.phase === newPhase) return;
    if ((appPhase === 'CONVERSATION' || appPhase === 'ENDED') && newPhase !== 'ENDED') {
      setConversationHistory([]);
      setCurrentTurn('NONE');
      setContext({});
    }
    const hash = `#${newPhase}`;
    const pageTitle = `BP ECHO - ${newPhase}`;
    window.history.pushState({ phase: newPhase }, pageTitle, hash);
    setAppPhase(newPhase);
  };

  const handleBack = () => {
    // This function defines the behavior for the in-app back button located in the header.
    // The browser's own back button (mouse or UI) is handled by the 'popstate' listener in the useEffect hook.
    switch (appPhase) {
      // From the main menu of the BP Echo app, go back to the main Welcome screen.
      case 'BP_ECHO_MAIN_MENU':
        navigate('WELCOME');
        break;

      // From the "Choose Your Teacher" page, the user could have come from multiple locations (e.g., Main Menu, Grade Selection).
      // Using the browser's history is the most intuitive way to go back to the correct previous page.
      case 'CHARACTER_SELECTION':
        window.history.back();
        break;

      // From the Grade Selection screen, the user should always return to the BP Echo main menu.
      case 'GRADE_SELECTION':
        navigate('BP_ECHO_MAIN_MENU');
        break;

      // When in a conversation or after it has ended...
      case 'CONVERSATION':
      case 'ENDED':
        // If it was a Grade 5/6 unit chat, go back to the Unit Selection screen for that grade.
        if (context.grade === 'grade5' || context.grade === 'grade6') {
          navigate('GRADE_SELECTION');
        }
        // Otherwise, it was a Free Chat, so go back to the BP Echo main menu.
        else {
          navigate('BP_ECHO_MAIN_MENU');
        }
        break;

      // For any other page, use the default browser history behavior as a reliable fallback.
      default:
        window.history.back();
        break;
    }
  };

  const savePlayerData = (newPlayerData) => {
    try {
      localStorage.setItem('bpEchoPlayerData', JSON.stringify(newPlayerData));
    } catch (error) { console.error("Failed to save player data", error); }
  };

  // ========= START: App.js - handleSaveUsername Function (REPLACEMENT) =========
  const handleSaveUsername = (username) => {
    // MODIFIED: Removed level, xp, and unlock points.
    // The player data now focuses on identity and conversation history.
    const newPlayerData = {
      username: username,
      conversationStats: {},
    };
    setPlayerData(newPlayerData);
    savePlayerData(newPlayerData);
    navigate('WELCOME');
  };

  const handleConversationComplete = () => {
    if (!context || !playerData) return;
    let conversationKey;
    if (context.isFreeChat) {
      conversationKey = context.conversationId;
    } else {
      conversationKey = `${context.grade}_unit${context.unit}`;
    }
    if (!conversationKey) return;
    setPlayerData(prevData => {
      const newStats = { ...prevData.conversationStats };
      newStats[conversationKey] = (newStats[conversationKey] || 0) + 1;
      const newPlayerData = { ...prevData, conversationStats: newStats };
      savePlayerData(newPlayerData);
      return newPlayerData;
    });
  };

  const handleUnitSelect = (grade, unit) => {
    const gradeNumber = grade === 'grade5' ? 5 : 6;
    const firstBotMessage = {
      speaker: 'bot',
      english: `Welcome to Grade ${gradeNumber}, Unit ${unit}. Let's begin!`,
      spoken: false // MODIFICATION: Add spoken flag
    };
    setConversationHistory([firstBotMessage]);
    setContext({
      isFreeChat: false,
      grade: grade,
      unit: unit,
      step: 'START_UNIT'
    });
    setErrorCount(0);
    navigate('CONVERSATION');
  };

  const handleHint = () => {
    if (!context.isFreeChat) return;
    let hintText = '';
    switch (context.step) {
      case 'AWAITING_NAME': hintText = "Try saying: My name is..."; break;
      case 'AWAITING_SPELLING': hintText = `Try spelling: ${context.name.toUpperCase().split('').join(' ')}`; break;
      case 'AWAITING_FEELING': hintText = "Try saying: I'm good."; break;
      case 'AWAITING_LIKE': hintText = `Try saying: I like ${getRandomItem(db.freeChat.vocabulary[context.topic])}.`; break;
      case 'AWAITING_SECOND_LIKE': hintText = "Try saying: Yes, I do. or No, I don't."; break;
      case 'AWAITING_HOBBY': hintText = "Try saying: My hobby is reading."; break;
      case 'AWAITING_SECOND_HOBBY': hintText = "Try saying: Yes, I do. or No, I don't."; break;
      default: break;
    }
    if (hintText) {
      const botMessage = { speaker: 'bot', english: hintText, spoken: false }; // MODIFICATION: Add spoken flag
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
    }
  };

  const handleAskNewQuestion = (transitionMessage) => {
    let newBotQuestion = '';
    const newContext = { ...context };

    // Determine the next topic or move to next question
    if (context.isFreeChat) {
      if (context.conversationId === 'FC_Likes') {
        const otherTopics = db.freeChat.topics.filter(t => t !== context.topic);
        const newTopic = getRandomItem(otherTopics);
        newContext.topic = newTopic;
        newBotQuestion = `What ${newTopic} do you like?`;
        newContext.initial_question = newBotQuestion;
        newContext.step = 'AWAITING_LIKE'; // Reset step for new topic
      } else if (context.conversationId === 'FC_Hobbies') {
        const allHobbies = Object.values(db.freeChat.hobbies_categorized).flat();
        const nextHobby = getRandomItem(allHobbies.filter(h => h.gerund !== context.user_hobby && h.gerund !== context.bot_last_hobby_asked));
        newBotQuestion = `Do you like ${nextHobby.gerund}?`;
        newContext.bot_last_hobby_asked = nextHobby.gerund;
        newContext.step = 'AWAITING_SECOND_HOBBY';
      }
    }

    if (newBotQuestion) {
      // M1 FIX: use the caller's transition line (e.g. "Oh, I see...") when one
      // is passed, instead of always overwriting it with a random phrase.
      const transition = transitionMessage || getRandomItem(db.freeChat.skipAndRetryResponses);
      const botMessage = { speaker: 'bot', english: `${transition} ${newBotQuestion}`, spoken: false };
      setErrorCount(0);
      setContext(newContext);
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
    } else {
      handleGiveUpAndEndTopic();
    }
  };

  const repeatLastBotMessage = () => {
    const lastBotMessage = conversationHistory.findLast(m => m.speaker === 'bot');
    if (lastBotMessage) {
      // If we have a specific initial question stored in context, use that for the reprompt.
      // This avoids repeating long greetings or transition phrases (e.g., "Oh, art? That sounds nice.")
      const textToRepeat = context.initial_question || lastBotMessage.english;
      const repeatMessage = { speaker: 'bot', english: textToRepeat, spoken: false };
      setConversationHistory(prev => [...prev, repeatMessage]);
      setCurrentTurn('BOT_TURN');
    }
  };

  const handleGiveUpAndEndTopic = () => {
    const giveUpMessage = getRandomItem(db.freeChat.finalGiveUpResponses);
    const botMessage = { speaker: 'bot', english: giveUpMessage, spoken: false }; // MODIFICATION: Add spoken flag
    setConversationHistory(prev => [...prev, botMessage]);
    setContext(prev => ({ ...prev, step: 'DONE' }));
    setCurrentTurn('BOT_TURN');
  };

  const handleSkip = () => {
    setErrorCount(0);
    setContext(prev => ({ ...prev, step: 'DONE' }));
    const userMessage = { speaker: 'user', english: '(Skipped)', japanese: '' };
    const botMessage = { speaker: 'bot', english: "Okay, let's talk next time!", spoken: false }; // MODIFICATION: Add spoken flag
    setConversationHistory(prev => [...prev, userMessage, botMessage]);
    setCurrentTurn('BOT_TURN');
  };

  const handleThemeSelect = (conversation) => {
    let newContext = { isFreeChat: true, conversationId: conversation.conversationId };
    let firstBotMessage = '';
    switch (conversation.conversationId) {
      case 'FC_Intro':
        firstBotMessage = `${getGreeting()}! My name is ${activeCharacter.name}. What’s your name?`;
        newContext.step = 'AWAITING_NAME';
        break;
      case 'FC_Likes':
        const randomTopic = getRandomItem(db.freeChat.topics);
        newContext.topic = randomTopic;
        const opener = getRandomItem(db.freeChat.likeDislikeOpeners);
        const initialQuestion = `What ${randomTopic} do you like?`;
        firstBotMessage = `${getGreeting()}! ${opener} ${initialQuestion}`;
        newContext.initial_question = initialQuestion;
        newContext.step = 'AWAITING_LIKE';
        break;
      case 'FC_Hobbies':
        const hobbyOpener = getRandomItem(db.freeChat.hobbyOpeners);
        firstBotMessage = `${getGreeting()}! ${hobbyOpener}`;
        // H1 FIX: Hobbies never set an initial_question, so reprompts and the
        // "Anyway, ..." reply showed the literal word "undefined".
        newContext.initial_question = "What do you like to do in your free time?";
        newContext.step = 'AWAITING_HOBBY';
        break;
      default: return;
    }
    setContext(newContext);
    setConversationHistory([{ speaker: 'bot', english: firstBotMessage, spoken: false }]); // MODIFICATION: Add spoken flag
    setErrorCount(0);
    navigate('CONVERSATION');
  };

  const handleCharacterSelect = (character) => {
    setActiveCharacter(character);
    navigate('BP_ECHO_MAIN_MENU');
  };

  const showCharacterSelection = () => {
    navigate('CHARACTER_SELECTION');
  };

  // ========= START: App.js - New BGM Handlers Section =========
  const handleStartEcho = () => {
    audioManager.playBGM('chatBgmMenu');
    navigate('BP_ECHO_MAIN_MENU');
  };

  const handleToggleMute = () => {
    const newMuteState = audioManager.toggleMute();
    setIsMuted(newMuteState);
  };
  // ========= END: App.js - New BGM Handlers Section =========


  // ========= START: App.js - Initialization Effects (REPLACE FIRST 2 useEffects) =========
  // Replace the first two useEffect hooks (Load Player Data and PopState) with this block.

  useEffect(() => {
    try {
      const savedData = localStorage.getItem('bpEchoPlayerData');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setPlayerData(parsedData);
        setAppPhase(getInitialPhase());
      } else {
        // MODIFIED: Auto-initialize "Guest" user immediately.
        // We no longer show the Username Setup screen.
        const defaultData = {
          username: 'Guest',
          conversationStats: {},
        };
        setPlayerData(defaultData);
        savePlayerData(defaultData);
        setAppPhase('WELCOME');
      }
    } catch (error) {
      console.error("Failed to load player data", error);
      // Fallback in case of error
      const defaultData = { username: 'Guest', conversationStats: {} };
      setPlayerData(defaultData);
      setAppPhase('WELCOME');
    }
  }, []);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.phase) {
        setAppPhase(event.state.phase);
      } else {
        // MODIFIED: Default fallback is now WELCOME, not USERNAME_SETUP
        setAppPhase('WELCOME');
      }
    };
    window.addEventListener('popstate', handlePopState);
    const initialPhase = getInitialPhase();
    const hash = `#${initialPhase}`;
    if (appPhase !== 'LOADING') {
      window.history.replaceState({ phase: initialPhase }, `BP ECHO - ${initialPhase}`, hash);
    }
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [playerData, appPhase]);
  // ========= END: App.js - Initialization Effects =========

  // ========= START: App.js - BGM Management Effect Hook Section =========
  useEffect(() => {
    const chatMenuPhases = [
      'BP_ECHO_MAIN_MENU',
      'CHARACTER_SELECTION',
      'GRADE_SELECTION'
    ];

    if (chatMenuPhases.includes(appPhase)) {
      audioManager.playBGM('chatBgmMenu');
    } else {
      // For any other phase (like WELCOME, CONVERSATION, etc.), stop the music.
      audioManager.stopBGM();
    }
  }, [appPhase]);
  // ========= END: App.js - BGM Management Effect Hook Section =========

  useEffect(() => {
    /*
      =======================================================================
      === DEVELOPMENT MODE: AUTHORIZATION CHECK IS DISABLED ===============
      =======================================================================
      The code inside this useEffect hook is commented out to prevent the
      React app from redirecting to the login page. This allows you to
      work on and test the component directly.

      *** CRITICAL: Before deploying to the live server (bplabo.jp), you ***
      *** MUST UNCOMMENT this block to re-enable security in the app.    ***
      =======================================================================
    */

    const checkAuthorization = () => {
      if (window.auth && window.auth.isLoggedIn()) {
        const currentUser = window.auth.getCurrentUser();
        const allowedRoles = ['admin', 'alt', 'jte', 'user', 'student'];

        if (!currentUser || !allowedRoles.includes(currentUser.role)) {
          alert('Access Denied: You do not have permission to view this page.');
          window.location.href = process.env.NODE_ENV === 'development'
            ? 'http://localhost:8000'
            : '/';
        }
      } else {
        window.location.href = process.env.NODE_ENV === 'development'
          ? 'http://localhost:8000/login.html'
          : '/login.html';
      }
    };
    checkAuthorization();

  }, []); // The empty array ensures this effect runs only once on mount.

  useEffect(() => {
    // MODIFIED: Removed the condition for 'PRONUNCIATION_CHALLENGE'.
    if (appPhase !== 'CONVERSATION' && appPhase !== 'ENDED') {
      speechRecognition.stopListening();
    }
  }, [appPhase, speechRecognition]);

  useEffect(() => {
    if (appPhase === 'CONVERSATION' && conversationHistory.length > 0 && currentTurn === 'NONE') {
      const lastMessage = conversationHistory[conversationHistory.length - 1];
      if (lastMessage.speaker === 'bot') {
        setCurrentTurn('BOT_TURN');
      }
    }
  }, [appPhase, conversationHistory, currentTurn]);

  // ========= CRITICAL FIX: This is the updated useEffect for speaking =========
  useEffect(() => {
    const latestMessageIndex = conversationHistory.length - 1;
    if (latestMessageIndex < 0) return;

    const latestMessage = conversationHistory[latestMessageIndex];

    if (currentTurn === 'BOT_TURN' && appPhase === 'CONVERSATION' && latestMessage.speaker === 'bot' && !latestMessage.spoken) {
      // 1. Immediately mark the message as "spoken" in the state
      const newHistory = [...conversationHistory];
      newHistory[latestMessageIndex].spoken = true;
      setConversationHistory(newHistory);

      // 2. Play the audio
      setBotState('talking');
      speak(latestMessage.english, activeCharacter.voice, () => {
        setBotState('idle');
        if (appPhase === 'CONVERSATION') { // Check phase again in case user navigated away
          if (context.step === 'DONE') {
            handleConversationComplete();
            navigate('ENDED');
            setCurrentTurn('NONE');
          } else {
            setCurrentTurn('USER_TURN');
          }
        }
      });
    }
  }, [currentTurn, conversationHistory, activeCharacter, context, appPhase, navigate]);
  // ========= END CRITICAL FIX =========

  // PUSH-TO-TALK: listening is now started/stopped by the student tapping the
  // mic button (see the render section). The old auto-listen + 7-second silence
  // timer was removed — it relied on desktop-only speech behavior and made the
  // bot answer too early (and miss words) on phones and tablets.

  useEffect(() => {
    if (appPhase !== 'CONVERSATION') return;
    if (currentTurn === 'REPROMPTING') {
      if (errorCount === 1) {
        repeatLastBotMessage();
      } else if (errorCount === 2) {
        handleHint();
      } else {
        if (context.conversationId === 'FC_Intro') {
          handleGiveUpAndEndTopic();
        } else {
          handleAskNewQuestion();
        }
      }
      return;
    }
    if (!finalTranscript || currentTurn !== 'USER_TURN') {
      return;
    }
    // PUSH-TO-TALK: the student already tapped "Done", which stopped listening
    // and committed this transcript — so just process it.

    let transcriptLower = finalTranscript.toLowerCase().trim();
    const fillerWords = ['um ', 'umm ', 'uh ', 'ah ', 'hmm ', 'eto ', 'ehh '];
    fillerWords.forEach(filler => {
      if (transcriptLower.startsWith(filler)) {
        transcriptLower = transcriptLower.substring(filler.length);
      }
    });
    const userMessage = { speaker: 'user', english: formatUserTranscript(finalTranscript), japanese: '' };
    setConversationHistory(prev => [...prev, userMessage]);

    // Check if it's a skip request
    if (transcriptLower.includes('skip please')) {
      handleSkip();
      resetTranscript();
      return;
    }

    // Check for user asking a question (e.g. "How about you?")
    const questionWords = ['who', 'what', 'where', 'when', 'why', 'how', 'do you'];
    const isUserQuestion = questionWords.some(word => transcriptLower.startsWith(word));
    const isExpectingAnswer = context.step && context.step !== 'DONE';
    if (isUserQuestion && isExpectingAnswer) {
      let botAnswer = "That's a good question.";
      if (transcriptLower.includes('how are you')) {
        botAnswer = getRandomItem(db.freeChat.botPositiveResponses);
      } else if (['how about you', 'what about you', 'and you'].some(p => transcriptLower.includes(p))) {
        // M3 FIX: a reciprocal follow-up ("How about you?") isn't a brand-new
        // question — answer it in kind rather than saying "That's a good question."
        botAnswer = context.step === 'AWAITING_FEELING'
          ? getRandomItem(db.freeChat.botPositiveResponses)
          : "I like lots of things!";
      } else if (transcriptLower.includes('do you like')) {
        botAnswer = Math.random() > 0.5 ? "Yes, I do." : "No, not really.";
      }
      const botResponseText = `${botAnswer} Anyway, ${context.initial_question}`;
      const botMessage = { speaker: 'bot', english: botResponseText, spoken: false };
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
      resetTranscript();
      return;
    }

    // Check for confusion or repeat request
    const isAwaitingPreference = context.step === 'AWAITING_SECOND_LIKE' || context.step === 'AWAITING_SECOND_HOBBY';
    const isConfusion = !isAwaitingPreference && (
      transcriptLower.includes("don't know") ||
      transcriptLower.includes("do not know") ||
      transcriptLower.includes("don't understand") ||
      transcriptLower.includes("do not understand")
    );

    if (isConfusion) {
      setErrorCount(prev => prev + 1);
      setCurrentTurn('REPROMPTING');
      resetTranscript();
      return;
    }

    // C2 FIX: match whole words/phrases only. Plain `.includes()` matched
    // letters buried inside other words — e.g. "no" inside "piano" and
    // "snowboarding", or "i do" inside "i don't" — flipping likes into
    // dislikes. Word boundaries (\b) require the phrase to stand on its own.
    const saidAny = (phrases) => phrases.some(phrase => {
      const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`\\b${escaped}\\b`, 'i').test(transcriptLower);
    });

    // Process positive/negative intent
    const isPositive = saidAny([
      'yes', 'yeah', 'yup', 'sure', 'uh-huh', 'i do',
      'i love', 'i like', "it's fun", "it's great", 'i really like'
    ]);

    const isNegative = saidAny([
      'no', 'nah', 'nope', 'uh-un', 'not really', "don't",
      "don't know", "do not know", "don't like", "do not like",
      "don't play", "do not play", "can't play", "cannot play"
    ]);

    // STANDALONE REPEAT CHECK: Only trigger if no other intent was found.
    const isRepeatRequestStandalone = db.freeChat.repeatRequestPhrases.some(phrase => {
      // If it's "what", only count as repeat if it's very short (don't capture "What about you?")
      if (phrase === 'what') {
        const words = transcriptLower.split(/\s+/);
        return words.length <= 2 && words.includes('what');
      }
      return transcriptLower.includes(phrase);
    });

    if (isRepeatRequestStandalone && !isPositive && !isNegative) {
      repeatLastBotMessage();
      resetTranscript();
      return;
    }

    let newContext = { ...context };
    let botResponseText = '';
    let wasSuccessful = true;
    const hasFollowUp = db.freeChat.followUpQuestions_Simple.some(phrase => transcriptLower.includes(phrase));

    switch (context.conversationId) {
      case 'FC_Intro':
        switch (context.step) {
          case 'AWAITING_NAME': {
            // I3 FIX: extractName recognises lead-ins ("call me", "it's", "name
            // is") and skips filler. Critically, when the speech was garbled and
            // no plausible name remains (e.g. mobile heard "My my my name my name
            // is."), it returns null — so we ASK AGAIN instead of inventing a
            // name out of a leftover filler word like "Is".
            const { name } = extractName(finalTranscript);
            if (!name) {
              // Couldn't make out a name — re-prompt up to 3 times, then move on.
              // Mirrors the soft-failure handling in AWAITING_SPELLING below.
              const newErrorCount = errorCount + 1;
              if (newErrorCount < 3) {
                setErrorCount(newErrorCount);
                const reprompt = getRandomItem(db.freeChat.shortReprompts);
                botResponseText = `${reprompt} What's your name?`;
                newContext.isSoftFailure = true;
              } else {
                wasSuccessful = false;
              }
              break;
            }
            newContext.name = name;
            botResponseText = `It's nice to meet you, ${name}! Could you spell that for me?`;
            newContext.step = 'AWAITING_SPELLING';
            newContext.initial_question = 'Could you spell that for me?';
            break;
          }
          case 'AWAITING_SPELLING': {
            // I1 FIX: "Could you spell that?" exists to CORRECT a mishearing.
            // The old code compared the spelling to the name we may have
            // misheard (e.g. heard "Dawn", so spelling D-O-N was rejected
            // forever — a dead end). Instead, ADOPT what the student spells as
            // the real name. Students answer two ways: by spelling out letters
            // ("D O N") or by repeating the name as a word ("Don") — handle both.
            const stopWords = new Set(['my', 'name', 'is', 'its', 'the', 'a', 'an', 'um', 'uh', 'well', 'spell', 'spelled', 'letter', 'letters', 'by', 'sure', 'okay', 'ok', 'so']);
            const tokens = finalTranscript
              .toLowerCase()
              .replace(/['’]/g, '')        // don't -> dont, it's -> its
              .replace(/[^a-z\s]/g, ' ')   // drop digits/punctuation
              .split(/\s+/)
              .filter(Boolean)
              .filter(t => !stopWords.has(t));
            const singleLetters = tokens.filter(t => t.length === 1);
            let spelled = '';
            if (singleLetters.length >= 2) {
              spelled = singleLetters.join('');                                  // "d o n" -> "don"
            } else if (tokens.length > 0) {
              spelled = tokens.reduce((a, b) => (b.length > a.length ? b : a));  // longest word -> "don"
            }

            if (spelled.length >= 2 && spelled.length <= 20) {
              // I4 FIX: a garbled spelling must not overwrite a name we already
              // heard correctly. reconcileSpelledName keeps the existing name
              // when the spelling is just a degraded version of it (had "John",
              // heard "J-J-O"), but still adopts a genuine correction (had
              // "Dawn", spelled "D-O-N" -> "Don"). spelledOut is derived from the
              // FINAL name so the "Got it — ..." readout matches it.
              const finalName = reconcileSpelledName(context.name, spelled);
              const spelledOut = finalName.toUpperCase().split('').join('-');
              newContext.name = finalName; // remember the confirmed name for the rest of the chat
              setConversationHistory(prev => {
                const newHistory = [...prev];
                newHistory[newHistory.length - 1].english = spelledOut;
                return newHistory;
              });
              botResponseText = `Got it — ${spelledOut}. Nice to meet you, ${finalName}! How are you?`;
              newContext.step = 'AWAITING_FEELING';
              newContext.initial_question = "How are you?";
            } else {
              // Couldn't make out any letters — ask once more, then move on.
              const newErrorCount = errorCount + 1;
              if (newErrorCount < 3) {
                setErrorCount(newErrorCount);
                const reprompt = getRandomItem(db.freeChat.shortReprompts);
                botResponseText = `${reprompt} Can you spell your name for me, letter by letter?`;
                newContext.isSoftFailure = true;
              } else {
                wasSuccessful = false;
              }
            }
            break;
          }
          case 'AWAITING_FEELING': {
            const positiveFeelings = ['good', 'great', 'happy', 'fine'];
            const negativeSadFeelings = ['sad', 'angry', 'tired'];
            const negativeSickFeelings = ['sick', 'stomachache', 'headache'];
            const temperatureHot = ['hot'];
            const temperatureCold = ['cold', 'chilly'];
            // M2 FIX: handle negation so "I'm not good" is no longer read as positive.
            const feelingNegated = /\b(not|never|don'?t|isn'?t|aren'?t|wasn'?t|can'?t|cannot)\b/.test(transcriptLower);
            const saysPositiveFeeling = positiveFeelings.some(feeling => transcriptLower.includes(feeling));
            if (saysPositiveFeeling && !feelingNegated) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.positive);
            } else if ((saysPositiveFeeling && feelingNegated) || negativeSadFeelings.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.negative_sad);
            } else if (negativeSickFeelings.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.negative_sick);
            } else if (temperatureHot.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.temperature_hot);
            } else if (temperatureCold.some(feeling => transcriptLower.includes(feeling))) {
              botResponseText = getRandomItem(db.freeChat.feelingResponses.temperature_cold);
            } else {
              botResponseText = "I see.";
            }
            if (hasFollowUp) {
              botResponseText += " " + getRandomItem(db.freeChat.botPositiveResponses);
            }
            newContext.step = 'DONE';
            break;
          }
          default:
            break;
        }
        break;
      case 'FC_Likes':
      case 'FC_Hobbies':
        const isLikesTopic = context.conversationId === 'FC_Likes';
        const currentStep = context.step;
        if ((isLikesTopic && currentStep === 'AWAITING_LIKE') || (!isLikesTopic && currentStep === 'AWAITING_HOBBY')) {
          // Check for Category Rejection (e.g., "I don't play sports")
          const categoryKeyword = isLikesTopic ? context.topic : (context.topic || 'hobbies');
          const isCategoryRejection = isNegative && transcriptLower.includes(categoryKeyword);

          if (isCategoryRejection) {
            handleAskNewQuestion("Oh, I see. Then let's talk about something else!");
            return;
          }

          // L1 FIX: also recognise NEGATIVE lead-ins ("I don't like ___"), not
          // just positive ones. Before, a negative answer matched nothing and
          // the whole raw sentence was echoed back, e.g. for "I don't like blue"
          // the bot said "You don't like i don't like blue? That's okay."
          const match = transcriptLower.match(/(?:i (?:really )?(?:don'?t|do not)(?: really)? (?:like|enjoy)|i (?:really )?hate|i (?:really )?(?:like|love|enjoy)|my hobby is)\s+(.+)/);
          let userResponseItem = match ? match[1].replace(/[.?]$/, '') : transcriptLower;

          // CLEANUP: Strip follow-up questions (e.g., "How about you?") from the extracted item
          db.freeChat.followUpQuestions_Simple.forEach(phrase => {
            const phraseIndex = userResponseItem.toLowerCase().indexOf(phrase.toLowerCase());
            if (phraseIndex !== -1) {
              userResponseItem = userResponseItem.substring(0, phraseIndex).trim();
            }
          });
          // Final trim and punctuation cleanup
          userResponseItem = userResponseItem.replace(/[.,?!]$/, '').trim();

          newContext.user_response = userResponseItem;

          let botFollowUpText = '';
          if (isNegative) {
            botFollowUpText = `I see. You don't like ${userResponseItem}? That's okay. `;
          } else {
            botFollowUpText = `Oh, ${userResponseItem}? That sounds nice. `;
          }

          let nextQuestion = '';
          if (isLikesTopic) {
            const vocab = db.freeChat.vocabulary[context.topic];
            const newItem = getRandomItem(vocab.filter(item => item.toLowerCase() !== userResponseItem.toLowerCase()));
            nextQuestion = `Do you like ${newItem}?`;
            newContext.bot_last_item_asked = newItem;
            newContext.step = 'AWAITING_SECOND_LIKE';
          } else {
            newContext.user_hobby = userResponseItem; // H2 FIX: record it so a later "new question" won't re-ask the student's own hobby
            const allHobbies = Object.values(db.freeChat.hobbies_categorized).flat();
            const nextHobby = getRandomItem(allHobbies.filter(h => h.gerund.toLowerCase() !== userResponseItem.toLowerCase()));
            nextQuestion = `Do you like ${nextHobby.gerund}?`;
            newContext.bot_last_hobby_asked = nextHobby.gerund;
            newContext.step = 'AWAITING_SECOND_HOBBY';
          }
          botResponseText = botFollowUpText + nextQuestion;
          newContext.initial_question = nextQuestion;
        } else if ((isLikesTopic && currentStep === 'AWAITING_SECOND_LIKE') || (!isLikesTopic && currentStep === 'AWAITING_SECOND_HOBBY')) {
          const lastAskedItem = isLikesTopic ? context.bot_last_item_asked : context.bot_last_hobby_asked;
          if (isPositive || isNegative) {
            if (isNegative && hasFollowUp) {
              botResponseText = `I see. Well, to answer your question, I do like ${lastAskedItem}.`;
            } else if (hasFollowUp) {
              const botLikesIt = Math.random() > 0.4;
              if (botLikesIt) {
                // L3 FIX: use the (previously unused) affirmation templates for variety.
                const affirmations = isLikesTopic ? db.freeChat.positiveAffirmations : db.freeChat.hobbyAffirmations;
                botResponseText = `${getRandomItem(affirmations).replace('{item}', lastAskedItem)} Thanks for asking!`;
              } else {
                botResponseText = `I don't really like ${lastAskedItem}, but that's okay!`;
              }
            } else {
              botResponseText = isNegative ? "I see. Thanks for sharing!" : "That's great. Thanks for sharing!";
            }
            newContext.step = 'DONE';
          } else {
            // L2 FIX: accept neutral/unsure answers ("maybe", "sometimes") instead
            // of failing the turn and reprompting. Wires up uncertainResponses.
            botResponseText = getRandomItem(db.freeChat.uncertainResponses);
            newContext.step = 'DONE';
          }
        }
        break;
      default:
        wasSuccessful = false;
        break;
    }
    if (wasSuccessful) {
      if (!newContext.isSoftFailure) {
        setErrorCount(0);
      } else {
        delete newContext.isSoftFailure;
      }
      setContext(newContext);
      const botMessage = { speaker: 'bot', english: botResponseText, spoken: false }; // MODIFICATION: Add spoken flag
      setConversationHistory(prev => [...prev, botMessage]);
      setCurrentTurn('BOT_TURN');
    } else {
      setErrorCount(prev => prev + 1);
      setCurrentTurn('REPROMPTING');
    }
    resetTranscript();
  }, [finalTranscript, currentTurn, context, errorCount, resetTranscript, appPhase]);
  // ========= END: App.js - Effect Hooks Section (UPDATED) =========


  // ========= START: App.js - Render Logic Section (REPLACEMENT) =========
  const renderContent = () => {
    if (appPhase === 'LOADING') {
      return <div>Loading BP ECHO...</div>;
    }
    // REMOVED: USERNAME_SETUP case

    if (appPhase === 'WELCOME') {
      const welcomeBackground = { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/welcome-bg.mp4` };
      return <WelcomeScreen
        onStartEcho={handleStartEcho}
        background={welcomeBackground}
      />;
    }

    if (appPhase === 'CHARACTER_SELECTION') {
      return <CharacterSelection onCharacterSelect={handleCharacterSelect} />;
    }
    if (appPhase === 'BP_ECHO_MAIN_MENU') {
      return <BpEchoMainMenu
        onStartFreeChat={(topic) => {
          const conversationIdMap = {
            introductions: "FC_Intro", 'likes-dislikes': "FC_Likes", hobbies: "FC_Hobbies"
          };
          const conversation = { conversationId: conversationIdMap[topic] };
          if (conversation.conversationId) {
            handleThemeSelect(conversation);
          }
        }}
        onConversationPractice={(grade) => {
          setSelectedGrade(grade);
          navigate('GRADE_SELECTION');
        }}
      />;
    }
    if (appPhase === 'GRADE_SELECTION') {
      return <GradeSelection grade={selectedGrade} onUnitSelect={handleUnitSelect} onBack={handleBack} />;
    }
    return ( // CONVERSATION or ENDED
      <>
        <AvatarDisplay botState={botState} activeCharacter={activeCharacter} />
        <div className="conversation-container">
          <ConversationWindow history={conversationHistory} subtitleMode={subtitleMode} />
          <div className="controls-footer">
            {appPhase === 'ENDED' ? (
              <div className="end-screen"><p>{t.conversationComplete}</p><button onClick={handleBack} className="start-button">{t.backToMenu}</button></div>
            ) : (
              <>
                {(currentTurn === 'USER_TURN' || currentTurn === 'REPROMPTING') && (
                  <div className="ptt-container">
                    <div className="ptt-row">
                      <button onClick={handleHint} className="hint-button">{t.hint}</button>
                      {!speechRecognition.isListening ? (
                        <button onClick={() => speechRecognition.startListening()} className="ptt-button ptt-start">
                          {t.tapToSpeak}
                        </button>
                      ) : (
                        <button onClick={() => speechRecognition.stopListening()} className="ptt-button ptt-done">
                          {t.done}
                        </button>
                      )}
                      <button onClick={handleSkip} className="hint-button skip-button">{t.skip}</button>
                    </div>
                    {speechRecognition.isListening && (
                      <div className="live-transcript">{speechRecognition.transcript || t.listening}</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  };
  // ========= END: App.js - Render Logic Section (REPLACEMENT) =========

  // ========= START: App.js - getBackgroundConfig Function (REPLACEMENT) =========
  const getBackgroundConfig = () => {
    switch (appPhase) {
      // These phases manage their own backgrounds or have none.
      case 'WELCOME':
        return { type: 'none' };
      // REMOVED: USERNAME_SETUP case

      // Use a video for the main menu.
      case 'BP_ECHO_MAIN_MENU':
        return { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/menu-bg.mp4` };

      // UPDATED: Use specific videos for each grade selection screen.
      case 'GRADE_SELECTION':
        return {
          type: 'video',
          src: selectedGrade === 'grade5'
            ? `${process.env.PUBLIC_URL}/assets/backgrounds/menu-grade-5-bg.mp4`
            : `${process.env.PUBLIC_URL}/assets/backgrounds/menu-grade-6-bg.mp4`
        };
      case 'CHARACTER_SELECTION':
        return { type: 'image', src: `${process.env.PUBLIC_URL}/assets/backgrounds/teachers-bg.jpeg` };
      case 'CONVERSATION':
      case 'ENDED':
        return { type: 'image', src: `${process.env.PUBLIC_URL}/assets/backgrounds/chat-bg.jpeg` };
      default:
        return { type: 'none' };
    }
  };
  // ========= END: App.js - getBackgroundConfig Function (REPLACEMENT) =========

  // ========= START: App.js - Main Component Return (REPLACEMENT) =========
  return (
    <div className="App" data-phase={appPhase}>
      <Background background={getBackgroundConfig()} />

      {/* MODIFIED: The check for 'PRONUNCIATION_CHALLENGE' is removed. */}
      {playerData && appPhase !== 'WELCOME' && (
        <header className="App-header">
          {(appPhase !== 'CHARACTER_SELECTION' && appPhase !== 'WELCOME') && (
            <button onClick={handleBack} className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor" />
              </svg>
            </button>
          )}
          {appPhase === 'CHARACTER_SELECTION' && (
            <button onClick={handleBack} className="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor" />
              </svg>
              Back
            </button>
          )}
          <div className="header-title-container">
            <img src={`${process.env.PUBLIC_URL}/assets/logo/bp-echo-logo_header.png`} alt="BP ECHO" className="header-logo" />
          </div>
          <div className="header-buttons-right">
            {(appPhase === 'CONVERSATION' || appPhase === 'ENDED') && (
              <button onClick={() => setShowSettings(true)} className="settings-button" title={t.settings} aria-label={t.settings}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/></svg>
              </button>
            )}
            {(appPhase === 'BP_ECHO_MAIN_MENU' || appPhase === 'GRADE_SELECTION') && (
              <button onClick={handleToggleMute} className="mute-button" title={isMuted ? "Unmute" : "Mute"}>
                {isMuted ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.82 18.87 13.61 18.64 14.34L20.05 15.75C20.68 14.63 21 13.36 21 12C21 7.72 18.28 4.21 14 3.23V5.29C17.18 6.25 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 17.83 14.83 18.06 14 18.18V20.24C15.38 19.93 16.63 19.26 17.68 18.36L19.73 20.41L21 19.14L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" /></svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 4.45v.2c0 .38.25.71.6.85C17.18 6.53 19 9.06 19 12s-1.82 5.47-4.4 6.5c-.35.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85C18.6 19.11 21 15.84 21 12s-2.4-7.11-5.79-8.4c-.58-.23-1.21.22-1.21.85z" /></svg>
                )}
              </button>
            )}
            {(appPhase === 'BP_ECHO_MAIN_MENU' || appPhase === 'GRADE_SELECTION') && (
              <button onClick={showCharacterSelection} className="teacher-button" title={t.chooseYourTeacher} aria-label={t.chooseYourTeacher}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
              </button>
            )}
          </div>
        </header>
      )}
      <main className="main-content-area">
        {!speechRecognition.hasRecognitionSupport ? (
          <p>{t.speechNotSupported}</p>
        ) : (
          renderContent()
        )}
      </main>

      {showSettings && (
        <div className="settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <h2>{t.settings}</h2>
              <button onClick={() => setShowSettings(false)} className="settings-close" aria-label="Close settings">✕</button>
            </div>
            <div className="settings-row">
              <span className="settings-label">{t.subtitles}</span>
              <SubtitleControls mode={subtitleMode} setMode={setSubtitleMode} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
  // ========= END: App.js - Main Component Return (REPLACEMENT) =========
}

export default App;
// ========= END: App.js (Corrected with Anti-Echo Logic) =========